import jsPDF from 'jspdf';

import image from 'assets/images/image.png';
import file from 'assets/images/file.png';
import pdf from 'assets/images/pdf.png';
import doc from 'assets/images/doc.png';
import docx from 'assets/images/docx.png';
import txt from 'assets/images/txt.png';

export const NEXT_LINE_OFFSET = 10;
export const NEXT_PAGE_OFFSET = 270;

const IMAGE_DIMENSION = 30;

export const addImageWithText = (
  pdf: jsPDF,
  x = 10,
  y = 10,
  image = '',
  text = '',
) => {
  if (y + IMAGE_DIMENSION > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }

  pdf.addImage(image, x, y, IMAGE_DIMENSION, IMAGE_DIMENSION);
  if (text.length > 0) {
    pdf.setFontSize(9);
    pdf.text(text, x + IMAGE_DIMENSION + 10, y - IMAGE_DIMENSION / 2);
    pdf.setFontSize(12);
  }
  y += IMAGE_DIMENSION + NEXT_LINE_OFFSET;

  if (y > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }

  return y;
};

const getImageFromExtension = (extension = '') => {
  switch (extension) {
    case 'png':
    case 'svg':
    case 'jpeg':
    case 'gif':
    case 'jpg':
      return image;
    case 'doc':
      return doc;
    case 'docx':
      return docx;
    case 'pdf':
      return pdf;
    case 'txt':
      return txt;
    default:
      return file;
  }
};

export const addImageWithUrl = (
  pdf: jsPDF,
  x = 10,
  y = 10,
  url = '',
  text = '',
) => {
  if (y > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }

  if (text.length > 0) {
    y = addText(pdf, x, y, text, true);
  }

  if (y + IMAGE_DIMENSION > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }

  const extension = url.split('.')?.pop()?.split(/#|\?/)?.[0] ?? '';
  const image = getImageFromExtension(extension);
  const filename = url.substring(url.lastIndexOf('/') + 1);

  pdf.addImage(image, x + 10, y - 5, IMAGE_DIMENSION, IMAGE_DIMENSION);
  addText(
    pdf,
    x + IMAGE_DIMENSION + 10,
    y + IMAGE_DIMENSION / 2 - 5,
    `${filename} (click to open)`,
    true,
    url,
  );

  y += IMAGE_DIMENSION + NEXT_LINE_OFFSET;
  pdf.setFontSize(9);
  pdf.setFontSize(12);

  return y;
};

export const addText = (
  pdf: jsPDF,
  x = 10,
  y = 10,
  text = '',
  isBold = false,
  url = '',
) => {
  if (y > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }
  const textToWrite = pdf.splitTextToSize(text, 160);

  if (isBold) {
    pdf.setFont(undefined as any, 'bold');
  }

  if (url.length > 0) {
    pdf.setTextColor('#0000EE');
    pdf.setFillColor('#0000EE');
    pdf.textWithLink(text, x, y, { url });
    const textWidth = pdf.getTextWidth(text);
    pdf.line(x, y + 1, x + textWidth, y + 1);
    pdf.setTextColor('#000000');
    pdf.setFillColor('#000000');
  } else {
    pdf.text(textToWrite, x, y);
  }

  if (textToWrite.length > 1) {
    y += Math.round(10 + textToWrite.length * 2.5);
  } else {
    y += NEXT_LINE_OFFSET;
  }

  return y;
};

export const addTwoText = (
  pdf: jsPDF,
  x = 25,
  y = 10,
  text1 = '',
  text2 = '',
) => {
  if (y > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }
  pdf
    .setFont(undefined as any, 'bold')
    .text(text1, x, y)
    .setFont(undefined as any, 'normal')
    .text(text2, x + pdf.getTextWidth(text1) + 5, y);

  y += NEXT_LINE_OFFSET;

  return y;
};

const wrapText = (
  context: CanvasRenderingContext2D,
  text = '',
  x = 10,
  y = 10,
  maxWidth = 600,
  lineHeight = 20,
) => {
  const words = text.split(' ');
  let line = '';
  let count = 10;
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = context.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      context.fillText(line, x, y);
      line = words[n] + ' ';
      y += lineHeight;
      count += 10;
    } else {
      line = testLine;
    }
  }
  context.fillText(line, x, y);
  count += 10;
  return count;
};
export const addTextAsImage = (pdf: jsPDF, x = 25, y = 10, text = '') => {
  if (y > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }
  // const textWidth = pdf.getTextWidth(text);
  const canvas = document.createElement('canvas');
  canvas.width = 1000;
  canvas.height = 120;

  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 13px times';
  ctx.fillStyle = '#000000';
  const lines = wrapText(ctx, text);
  const textImg = canvas.toDataURL('image/png');

  if (y + 30 > NEXT_PAGE_OFFSET) {
    addNewPage(pdf);
    y = 20;
  }

  pdf.addImage(textImg, x, y, 280, 40);

  y += lines - 10;

  return y;
};

export const addNewPage = (pdf: jsPDF) => {
  pdf.addPage();
  addBorderAndFooter(pdf);
};

export const addBorderAndFooter = (pdf: jsPDF) => {
  pdf.rect(10, 10, 190, 275);
  pdf.setFontSize(11);
  pdf.setTextColor('#000');
  pdf.setFont(undefined as any, 'normal');
  pdf.text(
    'Note: This report is strictly for internal client circulation. Copyright: Fieldmate Pvt Ltd',
    30,
    280,
  );
  pdf.setFontSize(12);
};

export const addTitle = (pdf: jsPDF, title: string, x = 25, y = 20) => {
  const tW = pdf.getTextWidth(title);
  const tX = Math.round((160 - tW) / 2);

  pdf
    .setTextColor('#604596')
    .setFont(undefined as any, 'bold')
    .text(title, x + tX, y)
    .setFont(undefined as any, 'normal')
    .setTextColor('#000');
};
